import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/App.css";

import { LangProvider, LANG_CODES, DEFAULT_LANG, isLangCode } from "@/i18n/LangContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { UserAuthProvider } from "@/contexts/UserAuthContext";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

import Home from "@/pages/Home";
import Games from "@/pages/Games";
import GameDetail from "@/pages/GameDetail";
import Verify from "@/pages/Verify";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import GoogleCallback from "@/pages/GoogleCallback";

import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminKeys from "@/pages/admin/AdminKeys";
import AdminLogs from "@/pages/admin/AdminLogs";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30 * 1000, retry: 1 } },
});

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <div className="relative z-10">{children}</div>
      <Footer />
    </>
  );
}

function LangGate({ children }) {
  // Validates :lang param; if unknown, redirect to default lang.
  const { lang } = useParams();
  if (!isLangCode(lang)) {
    return <Navigate to={`/${DEFAULT_LANG}`} replace />;
  }
  return children;
}

function LocalizedRoutes() {
  const publicRoutes = [
    { path: "", element: <Home /> },
    { path: "games", element: <Games /> },
    { path: "games/:id", element: <GameDetail /> },
    { path: "verify", element: <Verify /> },
    { path: "faq", element: <FAQ /> },
    { path: "contact", element: <Contact /> },
    { path: "signin", element: <SignIn /> },
    { path: "signup", element: <SignUp /> },
    { path: "forgot-password", element: <ForgotPassword /> },
    { path: "reset-password", element: <ResetPassword /> },
    { path: "profile", element: <Profile /> },
    { path: "auth/google", element: <GoogleCallback /> },
  ];
  return (
    <Routes>
      {/* Admin — language-agnostic */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route path="login" element={<AdminLogin />} />
        <Route index element={<Dashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="suppliers" element={<AdminSuppliers />} />
        <Route path="keys" element={<AdminKeys />} />
        <Route path="logs" element={<AdminLogs />} />
      </Route>

      {/* Localized public routes: /:lang/... */}
      <Route
        path="/:lang/*"
        element={
          <LangGate>
            <PublicLayout>
              <Routes>
                {publicRoutes.map((r) => (
                  <Route key={r.path} path={r.path} element={r.element} />
                ))}
                <Route path="*" element={<Navigate to="" replace />} />
              </Routes>
            </PublicLayout>
          </LangGate>
        }
      />

      {/* Legacy no-lang URLs are auto-redirected by LangProvider effect.
          As a fallback, catch-all -> default language. */}
      <Route path="*" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <LangProvider>
          <UserAuthProvider>
            <AuthProvider>
              <LocalizedRoutes />
              <Toaster position="top-right" theme="dark" richColors closeButton />
            </AuthProvider>
          </UserAuthProvider>
        </LangProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

// Also export code list — some legacy imports may use it.
export { LANG_CODES };
